import { useCallback, useContext, useEffect, useState } from 'react';
import { FaCalendarAlt, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import Navbar from '../../components/Navbar';
import { AuthContext } from '../../Context/AuthContext';
import axiosInstance from '../../utils/axiosConfig';

const AnswersDashboard = () => {
  const { isAuthenticated } = useContext(AuthContext);
  const [assessments, setAssessments] = useState([]);
  const [filteredAssessments, setFilteredAssessments] = useState([]);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [selectedAssessments, setSelectedAssessments] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [loading, setLoading] = useState(true);
  const [noResults, setNoResults] = useState(false);

  const fetchUser = useCallback(async () => {
    if (isAuthenticated) {
      setLoading(true);
      try {
        console.log('[AnswersDashboard] Fetching assessments');
        const response = await axiosInstance.get("/user/patient/nurse-chat/answers");
        console.log('[AnswersDashboard] Response:', response.data);
        
        const assessmentData = response.data.assessments || response.data.data || [];
        console.log('[AnswersDashboard] Assessment count:', assessmentData.length);
        
        if (assessmentData.length > 0) {
          console.log('[AnswersDashboard] Sample assessment:', assessmentData[0]);
        }

        const sortedAssessments = assessmentData.sort((a, b) => {
          const dateA = parseTimestampToDate(a.timestamp || a.createdAt)?.getTime() || 0;
          const dateB = parseTimestampToDate(b.timestamp || b.createdAt)?.getTime() || 0;
          return dateB - dateA;
        });

        setAssessments(sortedAssessments);
        setFilteredAssessments(sortedAssessments);
        setNoResults(sortedAssessments.length === 0);
      } catch (error) {
        console.error('[AnswersDashboard] Error fetching assessments:', error);
        console.error('[AnswersDashboard] Error details:', error.response?.data || error.message);
        setAssessments([]);
        setFilteredAssessments([]);
        setNoResults(true);
      } finally {
        setLoading(false);
      }
    } else {
      console.log('[AnswersDashboard] Not authenticated, redirecting to login');
      window.location.href = "/login";
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchUser();
    }
  }, [isAuthenticated, fetchUser]);

  const getProName = (name) => {
    if (name === 'preprocedure') {
      return 'Pre Procedure';
    }
    if (name === 'postprocedure') {
      return 'Post Procedure';
    }
    if (name === 'viral-fever') {
      return 'Viral Fever Assessment';
    }
    if (name === 'lscs-pregnancy') {
      return 'LSCS Pregnancy Assessment';
    }
    return name ? name.charAt(0).toUpperCase() + name.slice(1).replace(/-/g, ' ') : 'Assessment';
  };

  const parseTimestampToDate = (value) => {
    if (!value) return null;
    if (value instanceof Date) return value;

    if (typeof value === 'number') {
      const date = new Date(value);
      return isNaN(date.getTime()) ? null : date;
    }

    const str = String(value).trim();

    if (/^\d{12,14}$/.test(str)) {
      const year = parseInt(str.slice(0, 4), 10);
      const month = parseInt(str.slice(4, 6), 10) - 1;
      const day = parseInt(str.slice(6, 8), 10);
      const hours = parseInt(str.slice(8, 10), 10) || 0;
      const minutes = parseInt(str.slice(10, 12), 10) || 0;
      const seconds = str.length >= 14 ? parseInt(str.slice(12, 14), 10) : 0;
      const date = new Date(year, month, day, hours, minutes, seconds);
      return isNaN(date.getTime()) ? null : date;
    }

    const parsed = new Date(str);
    return isNaN(parsed.getTime()) ? null : parsed;
  };

  const formatDateForThumbnail = (timestamp) => {
    const date = parseTimestampToDate(timestamp);
    if (!date) return { day: 'N/A', month: 'N/A' };

    return {
      day: date.getDate().toString(),
      month: date.toLocaleString('en-US', { month: 'short' }).toUpperCase()
    };
  };

  function convertToHumanReadable(value) {
    const date = parseTimestampToDate(value);
    if (!date) return '';

    return date.toLocaleString('en-US', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  const handleFromDateChange = (e) => {
    setFromDate(e.target.value);
  };

  const handleToDateChange = (e) => {
    setToDate(e.target.value);
  };

  const handleSearch = () => {
    let filtered = assessments;

    if (fromDate || toDate) {
      filtered = assessments.filter((assessment) => {
        const timestamp = parseTimestampToDate(assessment.timestamp || assessment.createdAt);
        if (!timestamp) return false;

        let from = fromDate ? new Date(fromDate) : new Date(0);
        let to = toDate ? new Date(toDate) : new Date();
        to.setHours(23, 59, 59, 999);

        if (fromDate && !toDate) {
          to = new Date();
          to.setHours(23, 59, 59, 999);
        }

        if (!fromDate && toDate) {
          from = new Date(0);
        }

        return timestamp >= from && timestamp <= to;
      });
    }

    setFilteredAssessments(filtered);
    setNoResults(filtered.length === 0);
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    setFromDate('');
    setToDate('');
    setFilteredAssessments(assessments);
    setNoResults(assessments.length === 0);
    setCurrentPage(1);
  };

  const toggleSelection = (item) => {
    console.log(item);
    setSelectedAssessments((prev) => {
      if (prev.some((selectedItem) => selectedItem._id === item._id)) {
        return prev.filter((selectedItem) => selectedItem._id !== item._id);
      } else {
        return [...prev, item];
      }
    });
  };

  const handleSelectUsersClick = () => {
    if (selectedAssessments.length === 0) {
      alert("Please select at least one assessment.");
      return;
    }
    // Store selected assessments in localStorage (or pass via query param)
    localStorage.setItem('selectedAssessments', JSON.stringify(selectedAssessments));
    window.location.href = "/dashboard/share-data/list"; // Adjust route as needed
  };

  const handleNavigate = (item) => {
    localStorage.setItem('selectedQuestion', JSON.stringify(item.data));
    if (item.timestamp) {
      localStorage.setItem('selectedAssessmentTimestamp', String(item.timestamp));
    } else {
      localStorage.removeItem('selectedAssessmentTimestamp');
    }
    window.location.href = `/dashboard/share-data/answers/${item._id}`;
  };

  // Pagination logic
  const totalPages = Math.ceil(filteredAssessments.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentAssessments = filteredAssessments.slice(startIndex, endIndex);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const getAssessmentTypeColor = (name) => {
    const colors = {
      'preprocedure': 'bg-green-100 border-green-300',
      'postprocedure': 'bg-blue-100 border-blue-300',
      'viral-fever': 'bg-red-100 border-red-300',
      'lscs-pregnancy': 'bg-purple-100 border-purple-300',
      'anesthesia': 'bg-yellow-100 border-yellow-300',
      'woundcare': 'bg-cyan-100 border-cyan-300'
    };
    return colors[name] || 'bg-gray-100 border-gray-300';
  };

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gray-50 pt-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header Section */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Assessment Dashboard</h1>
                <p className="text-gray-600 mt-1">View and manage your assessment records</p>
              </div>
              <button
                className="px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg shadow-sm hover:bg-blue-700 transition-colors duration-200"
                onClick={handleSelectUsersClick}
              >
                Share Selected ({selectedAssessments.length})
              </button>
            </div>
          </div>

          {/* Filters Section */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">From Date</label>
                <div className="flex items-center border border-gray-300 rounded-lg p-3 bg-white h-12">
                  <FaCalendarAlt className="text-gray-400 mr-2 flex-shrink-0 align-middle" />
                  <input
                    type="date"
                    className="flex-1 outline-none bg-transparent border-none focus:ring-0 focus:outline-none text-gray-900 leading-none"
                    value={fromDate}
                    onChange={handleFromDateChange}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">To Date</label>
                <div className="flex items-center border border-gray-300 rounded-lg p-3 bg-white h-12">
                  <FaCalendarAlt className="text-gray-400 mr-2 flex-shrink-0 align-middle" />
                  <input
                    type="date"
                    className="flex-1 outline-none bg-transparent border-none focus:ring-0 focus:outline-none text-gray-900 leading-none"
                    value={toDate}
                    onChange={handleToDateChange}
                  />
                </div>
              </div>
              <div className="flex items-end">
                <button
                  className="w-full h-12 px-6 bg-blue-600 text-white font-semibold rounded-lg shadow-sm hover:bg-blue-700 transition-colors duration-200"
                  onClick={handleSearch}
                >
                  Search
                </button>
                <button
                  className="w-full h-12 px-6 mt-3 bg-gray-200 text-gray-800 font-semibold rounded-lg shadow-sm hover:bg-gray-300 transition-colors duration-200"
                  onClick={handleResetFilters}
                >
                  Reset
                </button>
              </div>
            </div>
          </div>

          {loading && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center mb-6">
              <div className="text-gray-600 text-lg">Loading assessments...</div>
            </div>
          )}

          {/* Assessment Tiles */}
          {!loading && (
          <div className="space-y-4 mb-6">
            {currentAssessments.map((item, index) => {
              const { day, month } = formatDateForThumbnail(item.timestamp);
              const assessmentName = item.name || item.assessment_name || item.assessment_type || item.type;
              const displayName = getProName(assessmentName);

              return (
                <div
                  key={index}
                  className={`w-full ${getAssessmentTypeColor(assessmentName)} border-2 rounded-xl shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer transform hover:scale-[1.01]`}
                  onClick={() => handleNavigate(item)}
                >
                  <div className="p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        {/* Circular Check Button */}
                        <div className="flex-shrink-0">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleSelection(item);
                            }}
                            className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${
                              selectedAssessments.some((selectedItem) => selectedItem._id === item._id)
                                ? 'bg-blue-600 border-blue-600 text-white'
                                : 'border-gray-300 hover:border-blue-400'
                            }`}
                          >
                            {selectedAssessments.some((selectedItem) => selectedItem._id === item._id) && (
                              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            )}
                          </button>
                        </div>

                        <div className="flex-shrink-0">
                          <div className="w-16 h-16 bg-white rounded-full shadow-sm flex flex-col items-center justify-center border-2 border-gray-200">
                            <div className="text-lg font-bold text-gray-800">{day}</div>
                            <div className="text-[10px] font-medium text-gray-500 uppercase tracking-wider">{month}</div>
                          </div>
                        </div>

                        {/* Assessment Details */}
                        <div className="flex-1">
                          <div className="mb-2">
                            <h3 className="text-xl font-bold text-gray-900">{displayName}</h3>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600">
                            <div>
                              <span className="font-medium">Date & Time:</span>
                              <span className="ml-2">{convertToHumanReadable(item.timestamp)}</span>
                            </div>
                            <div>
                              <span className="font-medium">Questions:</span>
                              <span className="ml-2">{item.data?.length || 0}</span>
                            </div>
                            <div>
                              <span className="font-medium">ID:</span>
                              <span className="ml-2 font-mono text-xs">{item._id?.slice(-8)}</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Arrow Icon */}
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 bg-white rounded-full shadow-sm flex items-center justify-center">
                          <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          )}

          {/* Pagination */}
          {!loading && totalPages > 1 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Showing {startIndex + 1} to {Math.min(endIndex, filteredAssessments.length)} of {filteredAssessments.length} assessments
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={handlePrevPage}
                    disabled={currentPage === 1}
                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FaChevronLeft className="w-4 h-4" />
                  </button>

                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter(page => {
                      // Show first page, last page, current page, and pages around current
                      return page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1;
                    })
                    .map((page, index, array) => (
                      <div key={page} className="flex items-center">
                        {index > 0 && array[index - 1] !== page - 1 && (
                          <span className="px-2 py-2 text-sm text-gray-500">...</span>
                        )}
                        <button
                          onClick={() => handlePageChange(page)}
                          className={`px-3 py-2 text-sm font-medium rounded-md ${
                            page === currentPage
                              ? 'text-blue-600 bg-blue-50 border border-blue-300'
                              : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          {page}
                        </button>
                      </div>
                    ))}

                  <button
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages}
                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FaChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Empty State */}
          {!loading && noResults && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaCalendarAlt className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No assessments found</h3>
              <p className="text-gray-600">Try adjusting your search criteria or date range.</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default AnswersDashboard;
